import type { ElementRef, OnDestroy } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';

import type { Node as ProsemirrorNode } from 'prosemirror-model';
import type { EditorView, NodeView } from 'prosemirror-view';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { I18nService } from '@core/i18n-legacy';
import { PlaceholderNode } from '@core/prosemirror-legacy';
import type { ProsemirrorAngularComponent } from '@core/prosemirror-legacy/angular';
import type { WysiwygLink } from '@core/wysiwyg-legacy';
import { LinkActionType, WysiwygLinkComponent, WysiwygLinkType } from '@core/wysiwyg-legacy';

@Component({
  templateUrl: 'link-item.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [WysiwygLinkComponent],
})
export class LinkItemComponent implements ProsemirrorAngularComponent, OnDestroy {
  private readonly i18Service = inject(I18nService);
  private readonly cdRef = inject(ChangeDetectorRef);

  public pmOnDestroy$ = new Subject<void>();

  @ViewChild('dom', { static: true }) public dom: ElementRef<HTMLElement>;

  private node: ProsemirrorNode;
  private view: EditorView;
  private getPos: () => number;
  private ngOnDestroy$ = new Subject<void>();
  @ViewChild(WysiwygLinkComponent) private link: WysiwygLinkComponent;

  public ngAfterViewInit() {
    this.handleWysiwygComponentOutput();
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public getNodeView(node: ProsemirrorNode, view: EditorView, getPos: () => number): NodeView {
    this.view = view;
    this.getPos = getPos;
    this.update(node);

    return {
      dom: this.dom.nativeElement,
      update: (node: ProsemirrorNode) => this.updateView(node),
      destroy: () => this.pmOnDestroy$.next(),
    };
  }

  private updateView(node: ProsemirrorNode) {
    if (PlaceholderNode.is(node)) {
      this.update(node);
      return true;
    }

    return false;
  }

  private handleWysiwygComponentOutput() {
    this.link.output$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(action => {
      if (action.type === LinkActionType.LinkDelete) {
        const transaction = this.view.state.tr.delete(
          this.getPos(),
          this.getPos() + this.node.nodeSize,
        );
        this.view.dispatch(transaction);
      }
    });
  }

  private update(node: ProsemirrorNode) {
    this.node = node;
    this.link.input$.next(<WysiwygLink>{
      ...node.attrs,
      type: WysiwygLinkType.Scheduler,
      label: node.attrs.href,
      name: node.attrs.name,
      url: node.attrs.href,
      noEdit: true,
    });
    this.cdRef.detectChanges();
  }
}
